import {KeyMessagesRequest, KeyMessagesResponse, KeyMessageWithScore} from "data/models/shared/key-messages";
import {getAuth} from "firebase/auth";
import {useAuthState} from "react-firebase-hooks/auth";
import {useObjectVal} from "react-firebase-hooks/database";
import {ref} from "firebase/database";
import {database, functions} from "hooks/firebase";
import {MultiSelectValue} from "data/models/survey/multi-choice";
import {useEffect, useMemo, useState} from "react";
import {isNullOrUndefined} from "helpers/is-null-or-undefined";
import {multiSelectValues} from "helpers/choice-values";
import {httpsCallable} from "firebase/functions";


export function useKeyMessages(): [KeyMessageWithScore[] | null, boolean, Error | null] {
  const auth = getAuth();
  const [user, userLoading, userError] = useAuthState(auth);
  const canvaAssetsPath = useMemo(() => `user-data/${user?.uid ?? ""}/user-surveys/canva-assets/all-features`, [user]);
  const [painsFearsIrritations, painsFearsIrritationsLoading, painsFearsIrritationsError] =
      useObjectVal<MultiSelectValue>((user && ref(database, `${canvaAssetsPath}//your-customers/pains-fears-irritations`)) ?? null);
  const [hopesAspirationsGoals, hopesAspirationsGoalsLoading, hopesAspirationsGoalsError] =
      useObjectVal<MultiSelectValue>((user && ref(database, `${canvaAssetsPath}/your-customers/hopes-aspirations-goals`)) ?? null);

  const [keyMessages, setKeyMessages] = useState<KeyMessagesResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const anyLoading = (
      userLoading
      || painsFearsIrritationsLoading
      || hopesAspirationsGoalsLoading
  );
  const anError = (
      userError
      ?? painsFearsIrritationsError
      ?? hopesAspirationsGoalsError
  );

  useEffect(() => {
    if (!isNullOrUndefined(anError)) {
      setError(anError);
      return;
    }
    if (anyLoading) {
      return;
    }
    if (isNullOrUndefined(painsFearsIrritations) || isNullOrUndefined(hopesAspirationsGoals)) {
      return;
    }

    const keyMessagesRequest: KeyMessagesRequest = {
      attributes: [...(multiSelectValues(painsFearsIrritations) ?? []), ...(multiSelectValues(hopesAspirationsGoals) ?? [])],
    };

    const keyMessagesFunction = httpsCallable<KeyMessagesRequest, KeyMessagesResponse>(functions, 'keyMessages');

    keyMessagesFunction(keyMessagesRequest).then(result => {
      setKeyMessages(result.data);
      setError(null);
    }).catch((error) => {
      setError(error as Error);
    });
  }, [
    anyLoading, anError,
    painsFearsIrritations,
    hopesAspirationsGoals,
  ]);

  return [keyMessages, anyLoading, error];

}

export function useKeyMessage(key: string | undefined): [KeyMessageWithScore | null, boolean, Error | null] {
  const [keyMessages, keyMessagesLoading, keyMessagesError] = useKeyMessages();

  if (isNullOrUndefined(key) || isNullOrUndefined(keyMessages) || keyMessagesLoading || keyMessagesError) {
    return [null, keyMessagesLoading, keyMessagesError];
  }

  return [keyMessages?.find(keyMessage => keyMessage.key === key) ?? null, keyMessagesLoading, keyMessagesError];
}
